import {
  ListMask,
  useScrollableMask,
} from '@euler/app/components/list/ListMask';
import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useAsyncStorage } from '@euler/functions/useAsyncStorage';
import { BiCardView, BiDashboardFilters } from '@euler/model/bi/dashboard';
import {
  BiExportPageSize,
  kBiExportPageSizeNames,
} from '@euler/model/bi/export';
import { BiPalette, kBiPaletteNames } from '@euler/model/bi/viz';
import { useServiceFactory } from '@euler/services/factory';
import { memo, useEffect, useRef, useState } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native';
import { BiButton } from '../../components/ui/BiButton';
import { BiChip } from '../../components/ui/BiChip';
import { BiPaletteSwatch } from '../../components/ui/BiPaletteSwatch';
import { biErrorMessage } from '../../functions/biErrors';
import { BiCardResults } from '../functions/biCardResults';
import { PaletteOption } from '../palette/PaletteOption';
import { ExportPreview, kBiExportPreviewNote } from './ExportPreview';

const kPalettes = Object.values(BiPalette);
const kSizes = Object.values(BiExportPageSize);
/** The size the viewer picked last, on this device. */
const kSizeKey = 'bi-export-page-size';
/** The dialog's cap on a wide screen: `BottomPanel` allows 74%. */
const kPanelShare = 0.74;
/** From this window width the preview sits beside the options. */
const kPreviewBesideFrom = 900;
/** A phone's two pages: the options, then the preview. */
const kPageCount = 2;
/** What a phone's pages leave clear under their content: the floating
 * dots and buttons. */
const kFloatingClearance = 128;

type Step =
  | { kind: 'choosing' }
  | { kind: 'sending' }
  | { kind: 'queued' }
  | { kind: 'refused'; message: string };

/** A palette to pick from the preview page: its name and colors. */
const PaletteChip = memo(
  ({
    palette,
    selected,
    onPress,
  }: {
    palette: BiPalette;
    selected: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Pressable
        accessibilityRole="radio"
        accessibilityLabel={`配色「${kBiPaletteNames[palette]}」`}
        accessibilityState={{ checked: selected }}
        onPress={onPress}
        style={({ pressed }) => ({
          gap: rpx(6),
          paddingHorizontal: rpx(10),
          paddingVertical: rpx(8),
          borderRadius: rpx(10),
          borderWidth: 1,
          borderColor: selected ? aiColors.primary : aiColors.border,
          backgroundColor: selected
            ? tint(aiColors.primary, 0.06)
            : aiColors.card,
          opacity: pressed ? 0.75 : 1,
        })}
      >
        <Typography.Text
          family={selected ? 'medium' : 'regular'}
          size={rpx(12)}
          color={selected ? aiColors.primary : aiColors.foreground}
        >
          {kBiPaletteNames[palette]}
        </Typography.Text>
        <BiPaletteSwatch palette={palette} size={7} />
      </Pressable>
    );
  },
);

/**
 * The palettes in a row that scrolls sideways within the content's edges
 * (the cards' below), fading at an edge with more beyond it.
 */
const PaletteChipRow = memo(
  ({
    palette,
    onPick,
  }: {
    palette: BiPalette;
    onPick: (palette: BiPalette) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      showLeadingMask,
      showTrailingMask,
      onLayout,
      onContentSizeChange,
      onScroll,
    } = useScrollableMask({ orientation: 'horizontal' });
    return (
      <ListMask
        leadingMask={showLeadingMask}
        trailingMask={showTrailingMask}
        location={0.06}
        style={{ marginHorizontal: rpx(24) }}
      >
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          onLayout={onLayout}
          onContentSizeChange={onContentSizeChange}
          onScroll={onScroll}
          contentContainerStyle={{ gap: rpx(8) }}
          accessibilityRole="radiogroup"
        >
          {kPalettes.map(option => (
            <PaletteChip
              key={option}
              palette={option}
              selected={option === palette}
              onPress={() => onPick(option)}
            />
          ))}
        </ScrollView>
      </ListMask>
    );
  },
);

/**
 * 导出 PDF: the size (paper or web) and the palette — the dashboard's own
 * preselected, which prints it as on screen — previewed on the cards the
 * dashboard loaded; then the export is queued under the range on screen
 * and the panel says it will push when done. On a wide screen the preview
 * sits beside the options; on a phone it is a second page, a swipe away.
 */
export const ExportPanel = memo(
  ({
    visible,
    dashboardId,
    filters,
    dashboardPalette,
    cards,
    order,
    results,
    onHistory,
    onClose,
  }: {
    visible: boolean;
    dashboardId: string;
    /** The filters the dashboard is viewed under. */
    filters: BiDashboardFilters;
    dashboardPalette: BiPalette;
    cards: BiCardView[];
    /** The cards in reading order. */
    order: string[];
    /** What the cards loaded: the preview draws from it. */
    results: BiCardResults;
    onHistory: () => void;
    onClose: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { height, width } = useWindowDimensions();
    const beside = !useIsMobileLayout() && width >= kPreviewBesideFrom;
    const { biService } = useServiceFactory();
    const [storedSize, setStoredSize] = useAsyncStorage<BiExportPageSize>(
      kSizeKey,
      BiExportPageSize.A4,
    );
    // A value an older build stored, or none, is A4.
    const size =
      storedSize && kSizes.includes(storedSize)
        ? storedSize
        : BiExportPageSize.A4;
    const [palette, setPalette] = useState(dashboardPalette);
    const [step, setStep] = useState<Step>({ kind: 'choosing' });
    // Wide screen: what sits above and below the palette list, measured —
    // neither depends on the list, so its cap never feeds back into
    // itself: the list gets what the dialog's cap leaves.
    const [above, setAbove] = useState(0);
    const [below, setBelow] = useState(0);
    const rest =
      above && below ? rpx(24) * 2 + rpx(14) * 2 + above + below : rpx(330);
    const listCap = Math.max(rpx(120), height * kPanelShare - rest);
    // Phone: the pager's size and the page on screen. A horizontal scroll
    // view does not stretch its pages to its height, so each page is given
    // it: without one, a page's own scroll view grows instead of scrolling.
    const pager = useRef<ScrollView>(null);
    const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
    const pageWidth = pageSize.width;
    const [page, setPage] = useState(0);
    // Each opening starts again: the dashboard's palette, nothing sent, the
    // options page.
    useEffect(() => {
      if (!visible) return;
      setPalette(dashboardPalette);
      setStep({ kind: 'choosing' });
      setPage(0);
    }, [visible, dashboardPalette]);

    const send = async () => {
      setStep({ kind: 'sending' });
      try {
        await biService.requestDashboardExport(dashboardId, {
          filters,
          pageSize: size,
          palette,
        });
        setStep({ kind: 'queued' });
      } catch (error) {
        console.error('BI export request failed', error);
        setStep({
          kind: 'refused',
          message: biErrorMessage(error, '导出没有提交成功，请稍后重试。'),
        });
      }
    };

    const showPage = (index: number) => {
      setPage(index);
      pager.current?.scrollTo({ x: index * pageWidth, animated: true });
    };
    const onPagerScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!pageWidth) return;
      const index = Math.round(e.nativeEvent.contentOffset.x / pageWidth);
      setPage(Math.min(kPageCount - 1, Math.max(0, index)));
    };

    const header = (
      <View style={{ gap: rpx(4), paddingHorizontal: rpx(24) }}>
        <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
          导出 PDF
        </Typography.Text>
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          {beside || step.kind === 'queued'
            ? '按当前的日期范围导出，完成后会通知你，可在导出记录中下载。'
            : page === 1
            ? `效果预览：${kBiExportPreviewNote}。`
            : '按当前的日期范围导出，完成后会通知你。左右滑动可预览效果。'}
        </Typography.Text>
      </View>
    );

    // Paper and web sizes in one row: the web ones carry their width.
    const sizes = (
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: rpx(8),
          paddingHorizontal: rpx(24),
        }}
      >
        {kSizes.map(option => (
          <BiChip
            key={option}
            label={kBiExportPageSizeNames[option]}
            selected={option === size}
            onPress={() => setStoredSize(option)}
          />
        ))}
      </View>
    );

    const paletteLabel = (
      <Typography.Text
        size={rpx(12)}
        color={aiColors.mutedForeground}
        style={{ paddingHorizontal: rpx(24) }}
      >
        {beside
          ? '配色（应用于PDF报表的配色方案）'
          : '配色（应用于PDF报表的配色方案，右滑预览）'}
      </Typography.Text>
    );

    const paletteList = (
      style: { maxHeight: number } | { flex: number },
      clearance = 0,
    ) => (
      <ScrollView
        // Full width, the inset inside: the scrollbar runs at the panel's
        // edge rather than over the options.
        style={style}
        contentContainerStyle={{
          gap: rpx(8),
          paddingHorizontal: rpx(24),
          paddingBottom: clearance,
        }}
        accessibilityRole="radiogroup"
      >
        {kPalettes.map(option => (
          <PaletteOption
            key={option}
            palette={option}
            selected={option === palette}
            onPress={() => setPalette(option)}
          />
        ))}
      </ScrollView>
    );

    const preview = (
      <ExportPreview
        cards={cards}
        order={order}
        results={results}
        palette={palette}
        dashboardPalette={dashboardPalette}
        bare={!beside}
        clearance={beside ? 0 : rpx(kFloatingClearance)}
      />
    );

    const refusal =
      step.kind === 'refused' ? (
        <Typography.Text
          size={rpx(12)}
          color={aiColors.destructive}
          style={{ paddingHorizontal: rpx(24) }}
        >
          {step.message}
        </Typography.Text>
      ) : null;
    // Floating, the buttons are wider and lifted off what scrolls under.
    const buttons = (floating = false) => (
      <>
        <BiButton
          title="取消"
          minWidth={floating ? rpx(120) : undefined}
          minHeight={floating ? rpx(44) : undefined}
          raised={floating}
          onPress={onClose}
        />
        <BiButton
          title="导出"
          tone="primary"
          minWidth={floating ? rpx(120) : undefined}
          minHeight={floating ? rpx(44) : undefined}
          raised={floating}
          loading={step.kind === 'sending'}
          onPress={() => void send()}
        />
      </>
    );
    const footer = (
      <>
        {refusal}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: rpx(10),
            paddingHorizontal: rpx(24),
          }}
        >
          {buttons()}
        </View>
      </>
    );

    const queued = (
      <>
        {header}
        <View style={{ gap: rpx(16), paddingHorizontal: rpx(24) }}>
          <Typography.Text size={rpx(14)} color={aiColors.foreground}>
            已开始导出，完成后会推送通知。
          </Typography.Text>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: rpx(10),
            }}
          >
            <BiButton title="完成" onPress={onClose} />
            <BiButton title="查看导出记录" tone="primary" onPress={onHistory} />
          </View>
        </View>
      </>
    );

    // Wide screen: the options on the left, on a light tint; the preview on
    // white on the right, pinned to their height and scrolling on its own,
    // its scrollbar at the dialog's edge.
    const besideLayout = (
      <View style={{ flexDirection: 'row' }}>
        <View
          style={{
            width: rpx(440),
            gap: rpx(14),
            paddingVertical: rpx(24),
            backgroundColor: aiColors.background,
            borderRightWidth: 1,
            borderRightColor: aiColors.border,
          }}
        >
          <View
            style={{ gap: rpx(14) }}
            onLayout={e => setAbove(Math.round(e.nativeEvent.layout.height))}
          >
            {header}
            {sizes}
            {paletteLabel}
          </View>
          {paletteList({ maxHeight: listCap })}
          <View
            style={{ gap: rpx(14) }}
            onLayout={e => setBelow(Math.round(e.nativeEvent.layout.height))}
          >
            {footer}
          </View>
        </View>
        <View style={{ flex: 1 }}>
          <View
            style={{
              position: 'absolute',
              top: rpx(24),
              bottom: rpx(24),
              left: 0,
              right: 0,
            }}
          >
            {preview}
          </View>
        </View>
      </View>
    );

    // Phone: two pages a swipe apart — the options, then the preview with
    // the palettes in a row above it — and one footer under both.
    const pagedLayout = (
      <View style={{ flex: 1, gap: rpx(12) }}>
        {header}
        <View
          style={{ flex: 1 }}
          onLayout={e =>
            setPageSize({
              width: Math.round(e.nativeEvent.layout.width),
              height: Math.round(e.nativeEvent.layout.height),
            })
          }
        >
          {pageWidth ? (
            <ScrollView
              ref={pager}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onScroll={onPagerScroll}
              scrollEventThrottle={100}
              style={{ flex: 1 }}
            >
              <View style={{ ...pageSize, gap: rpx(14) }}>
                {sizes}
                {paletteLabel}
                {paletteList({ flex: 1 }, rpx(kFloatingClearance))}
              </View>
              <View style={{ ...pageSize, gap: rpx(10) }}>
                <PaletteChipRow palette={palette} onPick={setPalette} />
                {preview}
              </View>
            </ScrollView>
          ) : null}
        </View>
        {/* Floating over the pages, centered: both scroll under them. */}
        <View
          pointerEvents="box-none"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: rpx(20),
            alignItems: 'center',
            gap: rpx(10),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
              gap: rpx(6),
              paddingHorizontal: rpx(8),
              paddingVertical: rpx(5),
              borderRadius: rpx(8),
              // Readable over whatever scrolls under them.
              backgroundColor: 'rgba(255,255,255,0.9)',
            }}
          >
            {Array.from({ length: kPageCount }, (_, index) => (
              <Pressable
                key={index}
                accessibilityRole="button"
                accessibilityLabel={index === 0 ? '导出选项' : '效果预览'}
                hitSlop={rpx(8)}
                onPress={() => showPage(index)}
              >
                <View
                  style={{
                    width: index === page ? rpx(16) : rpx(6),
                    height: rpx(6),
                    borderRadius: rpx(3),
                    backgroundColor:
                      index === page ? aiColors.primary : aiColors.border,
                  }}
                />
              </Pressable>
            ))}
          </View>
          {refusal ? (
            <View
              style={{
                backgroundColor: aiColors.card,
                borderRadius: rpx(10),
                paddingVertical: rpx(6),
              }}
            >
              {refusal}
            </View>
          ) : null}
          <View style={{ flexDirection: 'row', gap: rpx(12) }}>
            {buttons(true)}
          </View>
        </View>
      </View>
    );

    const choosing = step.kind !== 'queued';
    return (
      <BottomPanel
        visible={visible}
        onClose={onClose}
        fitContent={beside || !choosing}
        tall={!beside && choosing}
        width={beside ? Math.min(width - rpx(64), rpx(1040)) : undefined}
      >
        <View
          style={{
            // Beside, each column pads itself: the left one's tint reaches
            // the dialog's edges.
            paddingTop: beside && choosing ? 0 : rpx(24),
            // A phone's pages run to the sheet's bottom, under the floating
            // buttons.
            paddingBottom: choosing ? 0 : rpx(24),
            gap: rpx(14),
            flex: !beside && choosing ? 1 : undefined,
          }}
        >
          {!choosing ? queued : beside ? besideLayout : pagedLayout}
        </View>
      </BottomPanel>
    );
  },
);
